import { useRef, useState, useEffect, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import ThermalReceipt, { ThermalReceiptRow, ThermalReceiptSection } from '../receipts/ThermalReceipt';
import { RECEIPT_PRINT_STYLES } from '../receipts/printStyles';
import storeLogo from '../../images/logo-los-portales.png';
import { dateToDisplayString, displayStringToDate, DATE_OPTIONS, TIME_CONFIG } from '../../helpers/roscas';
import { formatPhoneForDisplay } from '../../helpers/phone';
import { getStoreLabel } from '../../constants/stores';
import roscasordersService from '../../services/roscaorders.service';
import printerService from '../../services/printer.service';
import './RoscaOrders.css';

export default function RoscaOrder({ rosca, onUpdate, onClose }) {
    const receiptRef = useRef();
    const [saving, setSaving] = useState(false);
    const [directPrintAvailable, setDirectPrintAvailable] = useState(false);
    const [printing, setPrinting] = useState(false);

    const reactToPrintFn = useReactToPrint({
        contentRef: receiptRef,
        ignoreGlobalStyles: true,
        pageStyle: RECEIPT_PRINT_STYLES,
    });

    useEffect(() => {
        printerService.isDirectPrintAvailable().then(setDirectPrintAvailable);
    }, []);

    // Edit state - initialize from rosca prop
    const [date, setDate] = useState(dateToDisplayString(rosca.date));
    const [time, setTime] = useState(rosca.time);
    const [firstName, setFirstName] = useState(rosca.firstName || '');
    const [lastName, setLastName] = useState(rosca.lastName || '');
    const [phoneNumber, setPhoneNumber] = useState(rosca.phoneNumber || '');
    const [amount, setAmount] = useState(parseInt(rosca.amount, 10) || 1);
    const [paid, setPaid] = useState(rosca.paid || 'No');
    const [cashier, setCashier] = useState(rosca.cashier || '');

    const dateFormat = displayStringToDate(date);
    const timeOptions = TIME_CONFIG[dateFormat] || [];

    function handleDateChange(newDate) {
        setDate(newDate);
        // Reset time if new date doesn't have current time option
        const newDateFormat = displayStringToDate(newDate);
        const newTimeOptions = TIME_CONFIG[newDateFormat] || [];
        if (!newTimeOptions.includes(time)) {
            setTime('');
        }
    }

    function incrementAmount() {
        setAmount(prev => prev + 1);
    }

    function decrementAmount() {
        setAmount(prev => Math.max(1, prev - 1));
    }

    async function handleSave() {
        if (!date || !time || !firstName || !lastName || !phoneNumber) {
            alert('Por favor completa todos los campos');
            return;
        }

        setSaving(true);
        try {
            const changes = {
                date: displayStringToDate(date),
                time,
                firstName,
                lastName,
                phoneNumber,
                amount: amount.toString(),
                paid,
                cashier
            };
            await roscasordersService.updateRoscaOrder(rosca.id, changes);

            // Notify parent to update local state
            if (onUpdate) {
                onUpdate({ ...rosca, ...changes });
            }
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error('Error updating rosca order:', error);
            alert('Error al guardar');
        } finally {
            setSaving(false);
        }
    }

    function handleCancel() {
        if (onClose) {
            onClose();
        }
    }

    async function handleDelete() {
        if (!window.confirm('¿Eliminar esta orden?')) return;

        try {
            await roscasordersService.deleteRoscaOrder(rosca.id);
            if (onUpdate) {
                onUpdate(null); // Signal deletion
            }
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error('Error deleting rosca order:', error);
            alert('Error al eliminar');
        }
    }

    const handlePrint = useCallback(async () => {
        if (directPrintAvailable) {
            const receiptData = {
                title: 'Rosca de Reyes',
                subtitle: rosca.store_id ? getStoreLabel(rosca.store_id) : '',
                meta: [
                    { label: 'Día', value: date },
                    { label: 'Hora', value: time },
                    { label: 'Pagado', value: paid },
                ],
                sections: [
                    {
                        title: 'Contacto',
                        rows: [
                            { label: 'Nombre', value: `${firstName} ${lastName}` },
                            { label: 'Teléfono', value: formatPhoneForDisplay(phoneNumber) },
                        ],
                    },
                    {
                        title: 'Orden',
                        rows: [
                            { label: 'Cantidad', value: `${amount} rosca${amount > 1 ? 's' : ''}` },
                            { label: 'Cajero', value: cashier },
                        ],
                    },
                ],
                footerNote: 'Gracias por tu orden',
            };

            setPrinting(true);
            const result = await printerService.printReceipt(receiptData);
            setPrinting(false);
            if (!result.success) {
                reactToPrintFn();
            }
        } else {
            reactToPrintFn();
        }
    }, [directPrintAvailable, rosca.store_id, date, time, paid, firstName, lastName, phoneNumber, amount, cashier, reactToPrintFn]);

    const canSave = date && time && firstName && lastName && phoneNumber && !saving;

    return (
        <div className="rosca-edit-modal">
            {/* RECOGIDA Section */}
            <div className="rosca-edit-section">
                <div className="rosca-edit-section-title">Recogida</div>
                <div className="rosca-edit-row">
                    <div>
                        <div className="rosca-label">Día</div>
                        <div className="rosca-edit-day-selector">
                            {DATE_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`rosca-day-btn ${date === opt.value ? 'selected' : ''}`}
                                    onClick={() => handleDateChange(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="rosca-label">Hora</div>
                        <div className="rosca-edit-time-grid">
                            {timeOptions.map(t => (
                                <button
                                    key={t}
                                    type="button"
                                    className={`rosca-time-btn ${time === t ? 'selected' : ''}`}
                                    onClick={() => setTime(t)}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                        {!date && <p className="rosca-hint">Selecciona un día primero</p>}
                    </div>
                </div>
            </div>

            {/* CANTIDAD Y PAGO Section */}
            <div className="rosca-edit-section">
                <div className="rosca-edit-section-title">Cantidad y Pago</div>
                <div className="rosca-edit-row">
                    <div>
                        <div className="rosca-label">Cantidad</div>
                        <div className="rosca-stepper">
                            <button
                                type="button"
                                className="rosca-stepper-btn"
                                onClick={decrementAmount}
                                disabled={amount <= 1}
                            >
                                −
                            </button>
                            <span className="rosca-stepper-value">{amount}</span>
                            <button
                                type="button"
                                className="rosca-stepper-btn"
                                onClick={incrementAmount}
                            >
                                +
                            </button>
                        </div>
                    </div>
                    <div>
                        <div className="rosca-label">Pagado</div>
                        <div className="rosca-edit-toggle-group">
                            <button
                                type="button"
                                className={`rosca-toggle-btn rosca-toggle-yes ${paid === 'Si' ? 'selected' : ''}`}
                                onClick={() => setPaid('Si')}
                            >
                                Sí
                            </button>
                            <button
                                type="button"
                                className={`rosca-toggle-btn rosca-toggle-no ${paid === 'No' ? 'selected' : ''}`}
                                onClick={() => setPaid('No')}
                            >
                                No
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* CONTACTO Section */}
            <div className="rosca-edit-section">
                <div className="rosca-edit-section-title">Contacto</div>
                <div className="rosca-edit-row">
                    <div className="rosca-edit-field">
                        <div className="rosca-label">Nombre</div>
                        <input
                            type="text"
                            className="rosca-input"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>
                    <div className="rosca-edit-field">
                        <div className="rosca-label">Apellido</div>
                        <input
                            type="text"
                            className="rosca-input"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>
                <div className="rosca-edit-row">
                    <div className="rosca-edit-field">
                        <div className="rosca-label">Teléfono</div>
                        <input
                            type="tel"
                            className="rosca-input"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                    </div>
                    <div className="rosca-edit-field">
                        <div className="rosca-label">Cajero</div>
                        <input
                            type="text"
                            className="rosca-input"
                            value={cashier}
                            onChange={(e) => setCashier(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Hidden receipt for printing */}
            <div className="rosca-print-hidden">
                <div ref={receiptRef}>
                    <ThermalReceipt
                        logoSrc={storeLogo}
                        logoAlt="Los Portales"
                        title="Rosca de Reyes"
                        subtitle={rosca.store_id ? getStoreLabel(rosca.store_id) : ''}
                        meta={[
                            { label: 'Día', value: date },
                            { label: 'Hora', value: time },
                            { label: 'Pagado', value: paid }
                        ]}
                        footerNote="Gracias por tu orden"
                    >
                        <ThermalReceiptSection title="Contacto">
                            <ThermalReceiptRow
                                label="Nombre"
                                value={`${firstName} ${lastName}`}
                            />
                            <ThermalReceiptRow
                                label="Teléfono"
                                value={formatPhoneForDisplay(phoneNumber)}
                            />
                        </ThermalReceiptSection>
                        <ThermalReceiptSection title="Orden">
                            <ThermalReceiptRow
                                label="Cantidad"
                                value={`${amount} rosca${amount > 1 ? 's' : ''}`}
                            />
                            <ThermalReceiptRow
                                label="Cajero"
                                value={cashier}
                            />
                        </ThermalReceiptSection>
                    </ThermalReceipt>
                </div>
            </div>

            {/* Actions */}
            <div className="rosca-edit-actions">
                <div className="rosca-edit-actions-left">
                    <button
                        type="button"
                        className="rosca-btn-danger"
                        onClick={handleDelete}
                    >
                        Borrar
                    </button>
                </div>
                <div className="rosca-edit-actions-right">
                    <button
                        type="button"
                        className="rosca-btn-secondary"
                        onClick={handlePrint}
                        disabled={printing}
                    >
                        {printing ? 'Imprimiendo...' : 'Imprimir'}
                    </button>
                    <button
                        type="button"
                        className="rosca-btn-secondary"
                        onClick={handleCancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="rosca-btn-primary"
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
